import { useState } from "react";
import { ActionMenu, Button, Card, IconButton, Stack, Title, Tooltip, useActions } from "@introist/react-foundation/v2";
import { OrderableList } from "../../../../foundation";
import { InputWithSave } from "../../../../foundation/templates/InputWithSave";
import { FormField, useFormFieldActions, useFormFields } from "../../useFormFields";
import { FormPage, useFormPageActions, useFormPages } from "../../useFormPages";
import { FormFieldIcon } from "./FormFieldIcon";
import { FormFieldList } from "./FormFieldList";

interface FormPagesEditorProps {
  formId: string;
  onCreateField: (pageId: string | null) => unknown;
  onOpenField: (id: string) => unknown;
}

const byPosition = (a: FormField, b: FormField) => a.position - b.position;

export const FormPagesEditor = ({ formId, onCreateField, onOpenField }: FormPagesEditorProps) => {
  const { formFields } = useFormFields(formId);
  const { formPages } = useFormPages(formId);
  const { reorder: reorderFields } = useFormFieldActions();
  const {
    create: createPage,
    update: updatePage,
    delete: deletePage,
    reorder: reorderPages,
    split
  } = useFormPageActions();

  const pages = formPages ?? [];

  // Zero pages: today's plain flat field list, with a single entry point to opt in.
  if (pages.length === 0) {
    return (
      <FormFieldList
        formId={formId}
        onCreateField={() => onCreateField(null)}
        onOpenField={onOpenField}
        extraActions={[
          {
            key: "split",
            title: "Split into pages",
            startAdornmentIcon: "form",
            onClick: () => split({ formId, title: "Page 1" })
          }
        ]}
      />
    );
  }

  const fields = formFields ?? [];
  const pageIds = new Set(pages.map(p => p.id));
  // Fields that lost their page (e.g. the page they were on got deleted) fold into the first page
  // instead of a separate "Ungrouped" section - every field always belongs to exactly one visible page.
  const orphanedFields = fields.filter(f => !f.pageId || !pageIds.has(f.pageId));

  const fieldsForPage = (pageId: string) => {
    const own = fields.filter(f => f.pageId === pageId);
    const extra = pageId === pages[0].id ? orphanedFields : [];
    return [...own, ...extra].sort(byPosition);
  };

  // Recompute the full cross-form field ordering so `position` stays globally
  // coherent (Slack renders fields in flat global-position order, unaware of pages).
  const flattenOrder = (overrides: { pageId?: string; fields?: FormField[]; pageOrder?: FormPage[] }) => {
    const order = overrides.pageOrder ?? pages;
    return order.flatMap(p =>
      (overrides.pageId === p.id ? overrides.fields! : fieldsForPage(p.id)).map(f => f.id)
    );
  };

  const movePage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= pages.length) return;

    const reordered = [...pages];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    reorderPages(
      formId,
      reordered.map(p => p.id)
    );
    reorderFields(formId, flattenOrder({ pageOrder: reordered }));
  };

  return (
    <Stack vertical>
      {pages.map((page, index) => (
        <PageSection
          key={page.id}
          title={page.title}
          fields={fieldsForPage(page.id)}
          droppableId={`droppable-page-${page.id}`}
          onOpenField={onOpenField}
          onCreateField={() => onCreateField(page.id)}
          onRename={title => updatePage({ id: page.id, update: { title } })}
          onDelete={() => deletePage(page.id, formId)}
          onMoveUp={index > 0 ? () => movePage(index, -1) : undefined}
          onMoveDown={index < pages.length - 1 ? () => movePage(index, 1) : undefined}
          onReorder={items => reorderFields(formId, flattenOrder({ pageId: page.id, fields: items }))}
          isOnlyPage={pages.length === 1}
        />
      ))}
      <Button
        variant="outlined"
        size="small"
        startIcon="plus"
        onClick={() => createPage({ formId, title: `Page ${pages.length + 1}`, description: null })}
      >
        Add page
      </Button>
    </Stack>
  );
};

interface PageSectionProps {
  title: string;
  fields: FormField[];
  droppableId: string;
  onOpenField: (id: string) => unknown;
  onCreateField: () => unknown;
  onReorder: (fields: FormField[]) => unknown;
  onRename: (title: string) => unknown;
  onDelete: () => unknown;
  onMoveUp?: () => unknown;
  onMoveDown?: () => unknown;
  isOnlyPage: boolean;
}

const PageSection = ({
  title,
  fields,
  droppableId,
  onOpenField,
  onCreateField,
  onReorder,
  onRename,
  onDelete,
  onMoveUp,
  onMoveDown,
  isOnlyPage
}: PageSectionProps) => {
  const { onConfirmAction } = useActions();
  const [titleValue, setTitleValue] = useState(title);

  const confirmDelete = onConfirmAction(onDelete, {
    title: "Delete page",
    description: isOnlyPage
      ? "Are you sure to delete this page? Its fields will stay on the form as a single page again."
      : "Are you sure to delete this page? Its fields will move to the first remaining page."
  });

  return (
    <Card style={{ padding: "var(--spacing-large)" }}>
      <Stack vertical>
        <Stack>
          <InputWithSave
            size="small"
            value={titleValue}
            onChange={setTitleValue}
            onSave={value => {
              if (value.trim() && value !== title) onRename(value.trim());
              else setTitleValue(title);
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
          <Stack gap="small" style={{ marginLeft: "auto", flexShrink: 0 }}>
            {onMoveUp && <IconButton icon="chevronUp" onClick={onMoveUp} dimmed />}
            {onMoveDown && <IconButton icon="chevronDown" onClick={onMoveDown} dimmed />}
            <ActionMenu
              options={[
                { key: "delete", title: "Delete page", startAdornmentIcon: "trash", onClick: confirmDelete }
              ]}
            />
          </Stack>
        </Stack>
        <OrderableList
          droppableId={droppableId}
          data={fields}
          onReorder={onReorder}
          empty={<Title style={{ color: "var(--color-foreground-dimmed)" }}>No fields yet</Title>}
          renderRow={field => (
            <Card onClick={() => onOpenField(field.id)}>
              <Stack>
                <FormFieldIcon fieldType={field.type} />
                <Title variant="bold">{field.shortTitle ?? field.title}</Title>
                {field.required && (
                  <Tooltip tooltip="This field is required">
                    <Title>*</Title>
                  </Tooltip>
                )}
              </Stack>
            </Card>
          )}
        />
        <Button variant="blended" size="small" startIcon="plus" onClick={onCreateField}>
          Add field
        </Button>
      </Stack>
    </Card>
  );
};
